/** @jsxImportSource @emotion/react */
import { useRouter } from "next/router";
import React, { useState } from "react";
import { toast } from "react-toastify";
import { useSetRecoilState } from "recoil";
import { api } from "../../../../api/GeneratedApi";
import { TestDriveBannerAtom } from "../../../../atoms/TestDriveBannerAtom";
import { UserAtom } from "../../../../atoms/UserAtom";
import { SERVER_URL } from "../../../../config";
import { IUserIRoute } from "../../../../generated/Api";
import { localStorageSetItem } from "../../../../utils/LocalStorage";
import PasscodeInput from "../../../PasscodeInput";
import StyledButton from "../../../StyledButton";
import { SignupFormSection } from "../../Form";
import {
  PasscodeButtonsWrapper,
  PasscodeContainer,
  PasscodeProps,
  PasscodeWrapper,
} from "../ThirdForm";

export default function ConfirmPasscode({
  setSelectedForm,
  form,
  handleChangeForm,
}: PasscodeProps) {
  const [error, setError] = useState<string>("");
  const [passcode, setPasscode] = useState(["", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const setUser = useSetRecoilState(UserAtom);
  const setTestDriveBanner = useSetRecoilState(TestDriveBannerAtom);
  const router = useRouter();
  const login = async ({ userUid, user, token }: any): Promise<void> => {
    if (!user.isActive) {
      toast("User is blocked", { type: "error" });
    } else if (!user) {
      toast("No user found!", { type: "error" });

      return;
    } else {
      const userToset = user as IUserIRoute & {
        billing: {
          admins?: number;
          externalUsers?: number;
          active?: boolean;
          testDriveExpiration?: number;
          testDriveActivated?: boolean;
        };
      };
      localStorageSetItem("USER_UID", user.userUid);
      localStorageSetItem("USER_TOKEN", token);

      localStorageSetItem("ADMIN_UID", user.adminUid || "");
      localStorageSetItem("ADMIN_AVATAR", user.avatar || "");

      localStorageSetItem("USER_EMAIL", "");
      localStorageSetItem("EXTRA_CARRIAGE", `${user.options?.extraCarriage}`);
      localStorageSetItem("FIRST_LINE_TITLE", `${user.options?.extraCarriage}`);
      setTestDriveBanner({
        show: !userToset.billing.active && userToset.billing.testDriveActivated,
        testDriveExpiration: userToset.billing.testDriveExpiration || 0,
      });
      setUser(userToset);
      router.replace("/complete-onboarding");
    }
  };

  async function handleClick() {
    if (
      passcode.includes("") ||
      passcode.join("") !== form.choosePasscode.join("")
    ) {
      toast("Incorrect passcode", { type: "error" });

      setError("Incorrect passcode");

      setIsLoading(false);
      return;
    }

    try {
      const responseReset = await api.auth.resetPasscodeCreate({
        type: "LEARNISTIC",
        phone: form.phone,
        tempPassword: form.temporaryPasscode.join(""),
        newPassword: passcode.join(""),
      });

      let responseResetJSON = responseReset.data as {
        status: string;
        data:
          | {
              ok: true;
              userUid: string;
            }
          | {
              ok: false;
            };
      };

      if (responseResetJSON.data.ok) {
        const response = await fetch(`${SERVER_URL}/auth`, {
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-type": "application/json",
          },
          body: JSON.stringify({
            ...form,
            password: passcode.join(""),
            type: "LEARNISTIC",
            aliasName: form.sendFromName,
            alias: form.domainSuffix + "@" + form.domain,
            userAppName: form.appName,
            company: "",
            address: "",
          }),
        });

        let responseJSON: {
          data: { userUid: string; user: IUserIRoute; token: string };
        } = await response.json();

        login({
          userUid: responseJSON.data.userUid,
          user: responseJSON.data.user,
          token: responseJSON.data.token,
        });
      } else {
        toast("Time expired", {
          type: "error",
        });
        setIsLoading(false);
      }
    } catch (error) {
      toast("Unexpected error", { type: "error" });
    }
  }

  return (
    <PasscodeContainer>
      <PasscodeWrapper>
        <PasscodeInput
          passcode={passcode}
          handleChangePasscode={(newPassCode) => setPasscode(newPassCode)}
          error={error !== ""}
          handleChangeError={(newError) => setError(newError)}
        />
      </PasscodeWrapper>

      <PasscodeButtonsWrapper>
        <StyledButton
          onClick={() => setSelectedForm(SignupFormSection.ChoosePasscode)}
          inverted
        >
          Back
        </StyledButton>
        <StyledButton
          disable={passcode.includes("")}
          isLoading={isLoading}
          onClick={() => {
            if (isLoading) return;
            setIsLoading(true);
            handleClick();
          }}
        >
          Next
        </StyledButton>
      </PasscodeButtonsWrapper>
    </PasscodeContainer>
  );
}
